import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const getAuthErrorMessage = (err) => {
  const detail = err.response?.data?.detail;
  if (!detail) return "Hiba történt";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(", ");
  if (typeof detail === "object") return detail.msg || detail.message || JSON.stringify(detail);
  return "Hiba történt";
};

export const loginInputClass =
  "w-full h-12 rounded-xl border border-slate-200 bg-slate-50 px-4 text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/25";

export const loginButtonClass =
  "w-full h-12 rounded-xl bg-[#F97316] text-white font-semibold shadow-lg shadow-orange-500/30 transition-colors hover:bg-[#EA6C0A] active:bg-[#DD6304] disabled:opacity-60 disabled:cursor-not-allowed";

export function LoginForm({ onSuccess, onForgot, onRegister, testIdPrefix = "login" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("A belépéshez fogadd el az adatkezelési tájékoztatót!");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/competition/login`, { email, password });
      localStorage.setItem("comp_token", res.data.token);
      localStorage.setItem("comp_user", JSON.stringify(res.data.user));
      toast.success("Sikeres bejelentkezés!");
      onSuccess?.(res.data.user, res.data.token);
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid={`${testIdPrefix}-form`}>
      <div>
        <label htmlFor={`${testIdPrefix}-email`} className="mb-1.5 block text-sm font-medium text-slate-700">
          E-mail-cím
        </label>
        <input
          id={`${testIdPrefix}-email`}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={loginInputClass}
          placeholder="pelda@email.hu"
          required
          data-testid="auth-email-input"
        />
      </div>

      <div>
        <label htmlFor={`${testIdPrefix}-password`} className="mb-1.5 block text-sm font-medium text-slate-700">
          Jelszó
        </label>
        <div className="relative">
          <input
            id={`${testIdPrefix}-password`}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`${loginInputClass} pr-12`}
            placeholder="••••••••"
            required
            data-testid="auth-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Jelszó elrejtése" : "Jelszó megjelenítése"}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-200/60 hover:text-slate-600"
            data-testid="auth-password-toggle"
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700" data-testid="auth-privacy-label">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded-md border-slate-300 accent-[#F97316]"
          data-testid="auth-privacy-checkbox"
        />
        <span>
          Elfogadom az{" "}
          <a
            href="/szabalyzat"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-[#EA6C0A] underline underline-offset-2 hover:text-[#C2410C]"
            data-testid="auth-privacy-link"
          >
            adatkezelési tájékoztatót
          </a>
          .
        </span>
      </label>

      <button type="submit" className={loginButtonClass} disabled={loading} data-testid="auth-submit-btn">
        {loading ? "Belépés..." : "Bejelentkezés"}
      </button>

      {(onRegister || onForgot) && (
        <div className="space-y-2 pt-1 text-center text-sm">
          {onRegister && (
            <p className="text-slate-700">
              Nincs még fiókod?{" "}
              <button type="button" onClick={onRegister} className="font-semibold text-[#EA6C0A] hover:text-[#C2410C]" data-testid="switch-to-register">
                Regisztrálj!
              </button>
            </p>
          )}
          {onForgot && (
            <button type="button" onClick={onForgot} className="text-slate-400 transition-colors hover:text-[#EA6C0A]" data-testid="forgot-password-link">
              Elfelejtett jelszó?
            </button>
          )}
        </div>
      )}
    </form>
  );
}

export default LoginForm;
